import { createServerFn } from "@tanstack/react-start";
import type { UIMessage } from "ai";
import { z } from "zod";

import type { NewThread } from "@/db/schema";
import { DatabaseError, ForbiddenError } from "@/lib/errors";
import {
	adminMiddleware,
	authMiddleware,
	creditsGuardFnMiddleware,
	unauthedMiddleware,
} from "@/routes/-middlewares";
import { startStudyOSChatInBackground } from "@/server/chat-runner";
import { generateThreadTitle } from "@/server/generate-thread-title";

const textPartSchema = z.object({
	type: z.literal("text"),
	text: z
		.string()
		.min(1)
		.max(3000, { message: "消息内容不能超过3000字符" })
		.meta({ description: "消息内容，最大3000字符" }),
});

const filePartSchema = z.object({
	type: z.literal("file"),
	url: z.string().meta({ description: "文件URL（S3或data URL）" }),
	mediaType: z.string().meta({ description: "文件MIME类型" }),
	filename: z.string().optional().meta({ description: "文件名" }),
});

const messagePartSchema = z.union([textPartSchema, filePartSchema]);

export const userMessageSchema = z
	.object({
		// id: z.string().meta({ description: "消息ID" }),
		role: z.literal("user").meta({ description: "消息角色" }),
		parts: z.array(messagePartSchema),
		metadata: z.record(z.string(), z.any()).optional().meta({
			description: "消息元数据",
		}),
	})
	.loose()
	.meta({
		description: "用户消息",
	});

// export const assistantMessageSchema = z.object({
// 	role: z.literal("assistant").meta({ description: "消息角色" }),
// 	parts: z.array(messagePartSchema),
// });

const createThreadInputSchema = z.object({
	message: userMessageSchema,
	// messages: z.array(z.any()),
	model: z.string().optional(),
	webSearch: z.boolean().optional(),
});

export const $createThread = createServerFn({
	method: "POST",
})
	.middleware([creditsGuardFnMiddleware])
	.inputValidator(createThreadInputSchema)
	.handler(async ({ context, data }) => {
		const { model } = data;
		await context.controllers.subscriptionController.assertCanUseChatModel(model ?? "lite");

		const message = await context.controllers.messageController.create({
			role: data.message.role,
			parts: data.message.parts,
		});

		if (!message) {
			throw new DatabaseError("Failed to create message");
		}

		const thread: NewThread =
			await context.controllers.threadController.createWithSourceMessage(message.id);

		const threadId = thread.id;

		if (!threadId) {
			throw new DatabaseError("Failed to create thread");
		}

		generateThreadTitle(data.message as unknown as UIMessage, context.user.id)
			.then((title) =>
				context.controllers.threadController.update(threadId, {
					title,
				}),
			)
			.catch(console.error);

		startStudyOSChatInBackground({
			uiMessages: [
				{
					id: message.id,
					role: "user",
					parts: data.message.parts,
				},
			] as unknown as UIMessage[],
			threadId,
			user: { id: context.user.id, role: context.user.role ?? null },
			modelId: model,
			persistSubmittedUserMessage: false,
		});

		return { threadId: thread.id };
	});

export const $getThread = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(z.object({ threadId: z.string() }))
	.handler(async ({ context, data }) => {
		const thread = await context.controllers.threadController.getById(data.threadId);

		if (!thread) {
			throw new Error("Thread not found");
		}

		if (thread.userId !== context.user.id) {
			throw new ForbiddenError();
		}

		// TODO: background trigger
		await context.controllers.threadController.update(data.threadId, {
			updatedAt: new Date(),
		});

		// Explicitly structure the return to help type inference
		return {
			id: thread.id,
			title: thread.title,
			userId: thread.userId,
			resourceId: thread.resourceId,
			createdAt: thread.createdAt,
			updatedAt: thread.updatedAt,
			messages: thread.messages.map((msg) => ({
				id: msg.id,
				role: msg.role,
				parts: msg.parts as Array<
					Record<string, string | number | boolean | null | object>
				>,
				createdAt: msg.createdAt,
				updatedAt: msg.updatedAt,
				metadata: msg.metadata,
			})),
		};
	});

export const $listThreads = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			page: z.number().optional(),
			pageSize: z.number().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		const { data: threads, ...pagination } =
			await context.controllers.threadController.list(data);

		return {
			...pagination,
			data: threads.map((thread) => ({
				...thread,
				isStreaming: false,
			})),
		};
	});

export const $updateThread = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.inputValidator(
		z.object({
			threadId: z.string(),
			title: z.string().optional(),
			summary: z.string().optional(),
			resourceId: z.string().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		const { threadId, ...updateData } = data;
		return context.controllers.threadController.update(threadId, updateData);
	});

export const $deleteThread = createServerFn({
	method: "POST",
})
	.middleware([authMiddleware])
	.inputValidator(z.object({ threadId: z.string() }))
	.handler(async ({ context, data }) => {
		await context.controllers.threadController.delete(data.threadId);

		return { success: true };
	});

/**
 * Get the latest thread for the current user.
 * Returns `{ threadId }` or `null` if no thread exists.
 */
export const $getLatestThread = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.handler(async ({ context }) => {
		const thread = await context.controllers.threadController.getLatestThread();
		return thread ? { threadId: thread.id } : null;
	});

/**
 * List featured threads (public, no auth required)
 */
export const $listFeaturedThreads = createServerFn({
	method: "GET",
})
	.middleware([unauthedMiddleware])
	.inputValidator(
		z.object({
			page: z.number().optional(),
			pageSize: z.number().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.threadController.listFeatured(data);
	});

/**
 * Update thread featured status (admin only)
 */
export const $updateThreadFeatured = createServerFn({
	method: "POST",
})
	.middleware([adminMiddleware])
	.inputValidator(
		z.object({
			threadId: z.string(),
			featured: z.boolean(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.threadController.update(data.threadId, {
			featured: data.featured,
		});
	});

/**
 * List all threads (admin only)
 */
export const $adminListThreads = createServerFn({
	method: "GET",
})
	.middleware([adminMiddleware])
	.inputValidator(
		z.object({
			page: z.number().optional(),
			pageSize: z.number().optional(),
			search: z.string().optional(),
		}),
	)
	.handler(async ({ context, data }) => {
		return context.controllers.threadController.listAll(data);
	});

export const $isThreadStreaming = createServerFn({
	method: "GET",
})
	.middleware([authMiddleware])
	.inputValidator(z.object({ threadId: z.string() }))
	.handler(async () => {
		return {
			data: { isStreaming: false, runId: null, success: true },
		};
	});
